import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const fmtShort = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '0';
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}M`;
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}jt`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}rb`;
  return String(Math.round(v));
};

const fmtCurrency = (v) => {
  if (v === null || v === undefined || isNaN(v)) return 'Rp.\u00a00';
  const hasFraction = Number(v) % 1 !== 0;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: hasFraction ? 2 : 0
  }).format(v).replace('Rp', 'Rp.');
};

const fmtPct = (v) => {
  if (v === null || v === undefined || isNaN(v) || !isFinite(v)) return '-';
  return `${(v * 100).toFixed(2)}%`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-md px-3 py-2 text-xs">
      <div className="font-semibold text-slate-800 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2" style={{ color: p.color }}>
          <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: p.color }} />
          <span className="text-slate-700">{p.name}:</span>
          <span className="font-medium">
            {p.dataKey === 'laba_per_modal' ? fmtPct(p.value) : fmtCurrency(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const YearlyModalChart = ({
  selectedYear,
  yearlySummary,
  loadingYearlySummary
}) => {
  if (!selectedYear) return null;

  const chartData = (yearlySummary || []).map((r) => ({
    name: r.month_label?.split(' ')[0]?.slice(0, 3) || r.month,
    modal: r.modal || 0,
    laba_per_modal: r.laba_per_modal || 0
  }));

  return (
    <Card className="border-2 border-amber-200 h-full" data-testid="yearly-modal-chart-card">
      <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 py-3">
        <CardTitle className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <TrendingUp size={16} />
          Modal vs Laba/Modal
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 pb-4 px-2">
        {loadingYearlySummary ? (
          <LoadingSpinner size="sm" color="amber" />
        ) : chartData.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-500">
            Tidak ada data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#64748b" />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 10 }}
                stroke="#64748b"
                tickFormatter={fmtShort}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 10 }}
                stroke="#64748b"
                tickFormatter={(v) => `${(v * 100).toFixed(1)}%`}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(251, 191, 36, 0.08)' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
              <Bar
                yAxisId="left"
                dataKey="modal"
                name="Modal"
                fill="#f59e0b"
                radius={[4, 4, 0, 0]}
                barSize={20}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="laba_per_modal"
                name="Laba / Modal"
                stroke="#0f766e"
                strokeWidth={2}
                dot={{ r: 3, fill: '#0f766e' }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default YearlyModalChart;
